import Axios from 'axios';
import { exceptionAnalysis } from '../util/exception';
import { invariant } from '../util/invariant';

export class Guard {
  private notifyMe: (() => void) | null = null;

  wait(): Promise<void> {
    invariant(this.notifyMe == null, 'Concurrent waits not supported');
    return new Promise(resolve => {
      this.notifyMe = resolve;
    });
  }

  waitWithAbort(signal: AbortSignal): Promise<void> {
    invariant(this.notifyMe == null, 'Concurrent waits not supported');
    if (signal.aborted) {
      return Promise.reject(signal.reason);
    }
    return new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        this.notifyMe = null;
        reject(signal.reason);
      };
      signal.addEventListener('abort', onAbort, { once: true });
      this.notifyMe = () => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      };
    });
  }

  notify() {
    const notifyMe = this.notifyMe;
    if (notifyMe != null) {
      this.notifyMe = null;
      notifyMe();
    }
  }
}

// Same as above except it supports concurrent waits.
export class MultiGuard {
  private guards: Guard[] = [];

  wait(): Promise<void> {
    const guard = new Guard();
    this.guards.push(guard);
    return guard.wait();
  }

  notifyOne() {
    if (this.guards.length > 0) {
      const [guard] = this.guards.splice(0, 1);
      guard.notify();
    }
  }

  notifyAll() {
    for (const guard of this.guards) {
      guard.notify();

    }
    this.guards = [];
  }
}

const neverCts = Axios.CancelToken.source();

export const neverCancel = neverCts.token;

/**
 * How to deliver a promise at a later time.
 */
export interface Deferred<T> {
  resolve: (result: T) => void;
  reject: (cause: any) => void;
}

export interface PromiseSource<T> {
  deferred: Deferred<T>;
  promise: Promise<T>;
}

/**
 * Create a promise that can be resolved or rejected by someone else in a
 * different context.
 */
export function defer<T>(): PromiseSource<T> {
  let deferred: Deferred<T> | null = null;
  const promise = new Promise<T>((resolve, reject) => {
    deferred = { resolve, reject };
  });
  promise.catch(() => {});
  invariant(deferred != null, 'Deferred should be assigned by now');
  return { promise, deferred };
}

function isFulfilled<T>(val: PromiseSettledResult<T>): val is PromiseFulfilledResult<T> {
  return val.status === 'fulfilled';
}

/**
 * A function starting an asynchronous operation.
 */
export type Task<T> = () => Promise<T>;

/**
 * When we want to wait for multiple promises,
 * we need to address the fact that a promise
 * may be rejected before we are awaiting which,
 * depending on which promise is rejected, can result
 * either in an unhandled promise rejection or in a
 * warning from node like this:
 *
 *      (node:160) PromiseRejectionHandledWarning: Promise rejection was handled asynchronously (rejection id: 2)
 *
 *
 * This utility object is meant to solve these problems in a safe way.
 * waitForAll will not return until all promises are rejected or resolved.
 * If all promises are resolved, it returns an array containing all the values.
 * Otherwise it throws an error detailing the result for each promise.
 */
export class PromiseUtil<T> {

  // Switch this parameter to true to get the unsafe behaviour
  // of just pushing promises to an array and use Promise.all() on it
  //
  // This is for testing purposes only
  constructor(private broken = false) {}

  private promises: Promise<T>[] = [];
  private promiseErrorReason: any[] = [];


  public push(...promises: Promise<T>[]) {
    const offset = this.promiseErrorReason.length;
    for (let i = 0; i < promises.length; ++i) {
      this.promiseErrorReason.push(undefined);
      if (!this.broken) {
        const p = promises[i];
        p.catch(e => this.promiseErrorReason[offset + i] = e ?? new Error('Rejected promise'));
      }
    }
    this.promises.push(...promises);
  }

  public getLength() {
    return this.promises.length;
  }

  async waitForAll(): Promise<T[]> {
    if (this.broken) {
      return await Promise.all(this.promises);
    }

    const results = await Promise.allSettled(this.promises);
    for (let i = 0; i < results.length; i++) {
      if (this.promiseErrorReason[i] !== undefined) {
        const rejection: PromiseRejectedResult = {
          status: 'rejected',
          // Let's transform the errors with exceptionAnalysis() to avoid
          // getting stringification errors in case the errors
          // are objects like Axios errors that could cause circular dependencies
          // problems when stringified directly
          reason: this.convertError(this.promiseErrorReason[i]),
        };
        results[i] = rejection;
      }
    }
    if (results.some(v => v.status === 'rejected')) {
      throw new Error(JSON.stringify(results));
    }

    return results.map(v => (v as PromiseFulfilledResult<T>).value);
  }

  private convertError(reason: any): string {
    if (typeof reason === 'string') {
      return reason;
    }

    return exceptionAnalysis(reason);
  }

  async waitForAllIgnoreRejections(): Promise<T[]> {
    if (this.broken) {
      return await Promise.all(this.promises);
    }
    const results = await Promise.allSettled(this.promises);
    return results.filter(isFulfilled).map(res => res.value);
  }
}

/**
 * Simple wrapper over PromiseUtil.waitForAll
 */
export function waitForAll<T>(proms: Promise<T>[]): Promise<T[]> {
  const pUtil = new PromiseUtil<T>();
  proms.forEach(p => pUtil.push(p));
  return pUtil.waitForAll();
}

/**
 * Utility which allows to wait for one of the promises to finish
 * in any order. The class supports interleaving push/waits.
 */
export class PromiseOneOf<T> {
  index: number = 0;
  promises: Record<number, Promise<void>> = { };
  results: Record<number, PromiseSettledResult<T>> = { };
  finished: number[] = [];
  guard: MultiGuard = new MultiGuard();

  push(promise: Promise<T>) {
    const index = this.index;
    this.promises[index] = promise
      .then((value) => { this.results[index] = { 'status': 'fulfilled', value }; })
      .catch((reason) => {
        this.results[index] = { 'status': 'rejected', reason };
      })
      .finally(() => { this._finish(index); });

    this.index += 1;
  }

  _finish(index: number) {
    this.finished.push(index);
    this.guard.notifyOne();
  }

  pendingCount(): number {
    return Object.keys(this.promises).length;
  }

  readyCount(): number {
    return this.finished.length;
  }

  async wait(): Promise<T> {
    while (this.finished.length === 0) {
      if (this.pendingCount() === 0) {
        throw new Error('empty PromiseOneOf');
      }
      await this.guard.wait();
    }

    const [first] = this.finished.splice(0, 1);
    delete this.promises[first];
    const result = this.results[first];
    delete this.results[first];
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      throw result.reason;
    }
  }

  async waitForAll(): Promise<T[]> {
    const values : T[] = [];
    const errors : any[] = [];

    while (this.pendingCount() > 0) {
      try {
        values.push(await this.wait());
      } catch (error) {
        errors.push(error);
      }
    }

    if (errors.length > 0) {
      throw errors;
    }
    return values;
  }
}

export type SettledPromise<T> = Promise<PromiseSettledResult<T>>;

export async function settlePromise<T>(p: Promise<T>): SettledPromise<T> {
  return Promise.allSettled([p]).then(settled => settled[0]);
}

export async function getSettledValueOrThrow<T>(p: SettledPromise<T>): Promise<T> {
  const result = await p;
  if (result.status === 'rejected') {
    throw result.reason;
  }
  return result.value;
}

/**
* @param promises An array of promises to be executed.
* @param batchSize The size of each batch.
* @returns A Promise that resolves to an array containing the results of all the promises.
*/
export async function batchPromises<T>(promises: Promise<T>[], batchSize: number): Promise<T[]> {
  let result: T[] = [];
  for (let i = 0; i < promises.length; i += batchSize) {
    const batch = promises.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch);
    result = result.concat(batchResults);
  }
  return result;
}
